import type { ApolloCache } from "@apollo/client";

/**
 * Record a favourite, or the loss of one, on the gateway's copy of a product in Apollo.
 *
 * `favorite` is what the product card itself reads. `favorite_count` is for the PDP, which loads
 * its product cache-first: without this, favouriting an item from a list and then going back to
 * its page shows a count that is one out. Both directions, unlike the hook this replaces, which
 * only ever counted down and so was wrong by one the other way round.
 *
 * The count is relative, which is safe under an optimistic response even though Apollo runs the
 * mutation's `update` twice: it throws the optimistic write away wholesale before replaying
 * `update` against the real result, so the second run counts from the original value.
 */
export const writeProductFavorite = (
  cache: ApolloCache<unknown>,
  productId: string,
  nextFavoriteId: string | null
) => {
  cache.modify({
    id: cache.identify({ __typename: "Product", id: productId }),
    fields: {
      favorite: () => (nextFavoriteId ? { id: nextFavoriteId } : null),
      favorite_count: (count: number) =>
        nextFavoriteId ? count + 1 : Math.max(0, count - 1),
    },
  });
};
